// LIFE — the top of the canvas. The Mountain is painted by the home behind
// everything (orbit.tsx); here we only choose what sits on it: the ASTRA
// compass or the Mountain controls (the Compass ⇄ Mountain pill). The compass
// is PINNED: its disc centre sits on the mountain's peak and never moves or
// scrolls. This screen holds only the world, the compass, its D-pad and the
// mode pill; the gold connector drops off the bottom edge toward the ORB
// screen below (a gold ▼ takes you there).
import React, { useEffect, useRef, useState } from 'react';
import { Animated, Pressable, StyleSheet, Text, View } from 'react-native';
import { CompassCore, type OrbCtx } from '../olmps/CompassView';
import type { GearId } from '../olmps/ascension';
import type { NowStep } from './useCurrentStep';
import type { Sector, Stage } from '../domain/olmps';
import type { OracleProject } from '../olmps/useOracleProjects';
import type { OracleSpine } from '../olmps/oracle-spine-data';
import type { Orbit } from './orbit';
import { MountainView } from './MountainView';
import { LinearGradient } from 'expo-linear-gradient';
import { Settings } from 'lucide-react-native';
import { GlassFill } from './ui';
import { ScreenHelp } from './ScreenHelp';
import { compassHelp } from '../olmps/help/compassHelp';
import { makeStyles, useV10 } from './theme';
import { tourBus, useTourTarget } from './onboarding';
import { readNavSync, writeNav } from '../lib/navPersist';

export type LifeMode = 'compass' | 'mountain';
export type TkneMode = 'on' | 'solo' | 'off';
/** Fraction of the orbit frame's height at which the summit sits. */
export const PEAK_FRAC = 0.37;

export function LifeView({
  userId,
  current,
  projects,
  spine,
  orbit,
  frameTop,
  frameSize,
  pillTop,
  bottomY,
  mode,
  onMode,
  onOpenTool,
  onOrbCtx,
  onNodePick,
  nodeText,
  frameLeft,
  anchorY,
  bottomInset,
  defaultGear,
  world,
  spinTo,
  onSectorTitle,
}: {
  userId: string | undefined;
  current: NowStep | null;
  projects: Record<Sector, OracleProject[]>;
  spine: OracleSpine | null;
  orbit: Orbit;
  /** Top of the orbit frame and its size, in this view's (= the panel's) coordinates. */
  frameTop: number;
  frameSize: number;
  pillTop: number;
  /** Lowest y the orb may reach. */
  bottomY: number;
  mode: LifeMode;
  onMode: (m: LifeMode) => void;
  onOpenTool: (tool: string) => void;
  onOrbCtx: (ctx: OrbCtx) => void;
  /** A compass node was tapped — go to the orb. */
  onNodePick: () => void;
  nodeText: (sector: Sector | null, stage: Stage) => string | null;
  /** x of the (screen-wide or wider) orbit frame. */
  frameLeft: number;
  /** Peak y: where the compass disc centre sits. */
  anchorY: number;
  bottomInset: number;
  defaultGear: GearId;
  /** The camera pitch the compass shares with the Mountain (see CompassCore). */
  world?: React.ComponentProps<typeof CompassCore>['world'];
  /** The sector deity's Play all turns the compass so this sector sits at 6 o'clock. */
  spinTo?: Sector | null;
  /** A sector title on the compass was tapped: open its overview. */
  onSectorTitle?: (s: Sector) => void;
}) {
  const t = useV10();
  const styles = useStyles();
  const compassPill = useTourTarget('life.compassPill');
  const mountainPill = useTourTarget('life.mountainPill');
  // Fill ⇄ Clear compass (Joel, 2026-10-15): the solid progress-fill disc or the original clear one.
  const [filled, setFilledState] = useState<boolean>(() => readNavSync<boolean>('v10compassFill') ?? true);
  const setFilled = (v: boolean) => {
    setFilledState(v);
    writeNav('v10compassFill', v);
  };
  // TKNE (the projects layer hanging under the disc): on / SOLO / off. SOLO
  // turns the compass from LIFE (five sectors) into TKNE mode — one spoke per
  // active project. Older saves stored a boolean.
  const [tkne, setTkneState] = useState<TkneMode>(() => {
    const v = readNavSync<TkneMode | boolean>('v10tkne');
    return v === 'solo' || v === 'off' || v === false ? (v === false ? 'off' : v) : 'on';
  });
  const setTkne = (v: TkneMode) => {
    setTkneState(v);
    writeNav('v10tkne', v);
    // Projects on: tilt down so their stacks hang in view; off: back to the flat top view (Joel, 2026-10-18).
    if (v === 'off') world?.onHideProjects?.();
    else world?.onShowProjects?.();
  };
  // The gear's dropdown. The onboarding tour points at the Compass/Mountain
  // toggle, so the menu holds itself open while that step is highlighted.
  const [menuOpen, setMenuOpen] = useState(false);
  const tourOpen = compassPill.highlighted || mountainPill.highlighted;
  const showMenu = menuOpen || tourOpen;

  // The disc fades with the mode; the orb and the mountain behind stay put.
  const discOpacity = useRef(new Animated.Value(mode === 'compass' ? 1 : 0)).current;
  useEffect(() => {
    Animated.timing(discOpacity, { toValue: mode === 'compass' ? 1 : 0, duration: 420, useNativeDriver: true }).start();
  }, [mode, discOpacity]);

  return (
    <View style={StyleSheet.absoluteFill} pointerEvents="box-none">
      {mode === 'mountain' ? (
        <View style={{ position: 'absolute', top: 0, bottom: 0, left: frameLeft, width: frameSize }} pointerEvents="box-none">
          <MountainView orbit={orbit} frameTop={frameTop} frameSize={frameSize} frameLeft={frameLeft} projects={projects} spine={spine} current={current} onOpenTool={onOpenTool} onOrbCtx={onOrbCtx} onExplore={onNodePick} />
        </View>
      ) : null}
      {/* Hidden compass must not swallow the Mountain's taps (D-pad, stops, markers). */}
      <View style={StyleSheet.absoluteFill} pointerEvents={mode === 'compass' ? 'box-none' : 'none'}>
      <CompassCore
        userId={userId}
        onOpenTool={onOpenTool}
        compact={false}
        pinned
        onOrbCtx={onOrbCtx}
        onNodePick={onNodePick}
        nodeText={nodeText}
        anchorY={anchorY}
        bottomY={bottomY}
        discOpacity={discOpacity}
        spinTo={spinTo}
        onSectorTitle={onSectorTitle}
        discHidden={mode !== 'compass'}
        defaultSector={current?.sector ?? null}
        defaultGear={defaultGear}
        world={world}
        filled={filled}
        tkne={tkne === 'on' ? true : tkne === 'solo' ? 'solo' : false}
      />
      </View>
      {/* Settings (Joel, 2026-10-17): the view / fill / TKNE toggles live in a gear dropdown. */}
      {menuOpen && !tourOpen ? <Pressable style={StyleSheet.absoluteFill} onPress={() => setMenuOpen(false)} /> : null}
      {/* "?" explainer: just left of the gear, same row. */}
      <ScreenHelp help={compassHelp({ onPace: t.onPace, behind: t.behind, lapsed: t.lapsed, faint: t.ivoryFaint })} style={[styles.help, { top: pillTop + 7 }]} />
      <Pressable
        onPress={() => setMenuOpen((o) => !o)}
        style={[styles.gear, { top: pillTop }, showMenu && styles.gearOn]}
        hitSlop={6}
        accessibilityRole="button"
        accessibilityLabel="Settings"
      >
        <GlassFill radius={999} />
        <Settings size={18} color={showMenu ? t.gold : t.ivorySoft} strokeWidth={2.2} />
      </Pressable>
      {showMenu ? (
        <View style={[styles.menu, { top: pillTop + 44 }]}>
          <GlassFill radius={18} />
          <Text style={styles.menuLabel}>View</Text>
          <View style={styles.modeRow}>
            {(['compass', 'mountain'] as LifeMode[]).map((m) => (
              <Pressable key={m} ref={m === 'compass' ? compassPill.onLayoutRef : mountainPill.onLayoutRef} onPress={() => {
                tourBus.emit(`life:mode:${m}`);
                onMode(m);
              }} style={[styles.modeBtn, mode === m && styles.modeBtnOn]} hitSlop={6}>
                <Text style={[styles.modeText, mode === m && { color: t.onGold }]}>{m === 'compass' ? 'Compass' : 'Mountain'}</Text>
              </Pressable>
            ))}
          </View>
          {mode === 'compass' ? (
            <>
              <Text style={styles.menuLabel}>Disc</Text>
              <View style={styles.modeRow}>
                {[true, false].map((v) => (
                  <Pressable key={v ? 'fill' : 'clear'} onPress={() => setFilled(v)} style={[styles.modeBtn, filled === v && styles.modeBtnOn]} hitSlop={6}>
                    <Text style={[styles.modeText, filled === v && { color: t.onGold }]}>{v ? 'Fill' : 'Clear'}</Text>
                  </Pressable>
                ))}
              </View>
              <Text style={styles.menuLabel}>Projects</Text>
              <View style={styles.modeRow}>
                {(['on', 'solo', 'off'] as TkneMode[]).map((v) => (
                  <Pressable key={`tkne-${v}`} onPress={() => setTkne(v)} style={[styles.modeBtn, tkne === v && styles.modeBtnOn]} hitSlop={6}>
                    <Text style={[styles.modeText, tkne === v && { color: t.onGold }]}>{v === 'on' ? 'TKNE' : v === 'solo' ? 'SOLO' : 'Off'}</Text>
                  </Pressable>
                ))}
              </View>
            </>
          ) : null}
        </View>
      ) : null}
    </View>
  );
}

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    gear: { position: 'absolute', right: 10, width: 40, height: 40, borderRadius: 999, borderWidth: 1, borderColor: t.navyLine, alignItems: 'center', justifyContent: 'center', overflow: 'hidden', zIndex: 21 },
    gearOn: { borderColor: t.gold, boxShadow: `0 0 14px ${t.goldGlow}` } as object,
    help: { position: 'absolute', right: 58, zIndex: 21 },
    menu: { position: 'absolute', right: 10, padding: 10, paddingTop: 6, gap: 6, borderRadius: 18, borderWidth: 1, borderColor: t.navyLine, overflow: 'hidden', zIndex: 21 },
    menuLabel: { color: t.ivorySoft, fontSize: 10, fontWeight: '800', letterSpacing: 1, textTransform: 'uppercase', opacity: 0.7, marginTop: 4, marginLeft: 4 },
    modeRow: { flexDirection: 'row', gap: 6, padding: 4, borderRadius: 999, borderWidth: 1, borderColor: t.navyLine, height: 36, overflow: 'hidden' },
    // Every pill segment is the same fixed width, so all three toggles line up.
    modeBtn: { width: 68, alignItems: 'center', justifyContent: 'center', borderRadius: 999 },
    modeBtnOn: { backgroundColor: t.gold, boxShadow: `0 0 14px ${t.goldGlow}` } as object,
    modeText: { color: t.ivorySoft, fontSize: 11, fontWeight: '800', letterSpacing: 0.3 },
  }),
);
